import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  Info,
  AlertOctagon,
  X,
  Clock,
  Bell,
  BellOff,
  Archive
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Alert {
  id: string;
  type: 'performance' | 'technique' | 'fatigue' | 'equipment';
  severity: 'low' | 'medium' | 'high';
  playerId: string;
  playerName: string;
  message: string;
  timestamp: Date;
}

interface AlertsPanelProps {
  alerts: Alert[];
  onDismissAlert: (alertId: string) => void;
  onDismissAll?: () => void;
  onToggleNotifications?: (enabled: boolean) => void;
}

export function AlertsPanel({ 
  alerts, 
  onDismissAlert, 
  onDismissAll,
  onToggleNotifications 
}: AlertsPanelProps) {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'low' | 'medium' | 'high'>('all');

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertOctagon className="w-4 h-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'low':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      performance: '📊',
      technique: '🎯',
      fatigue: '😴',
      equipment: '⚙️',
    };
    return icons[type as keyof typeof icons] || '📢';
  };

  const filteredAlerts = alerts.filter(alert => 
    filterSeverity === 'all' || alert.severity === filterSeverity
  );

  const sortedAlerts = filteredAlerts.sort((a, b) => {
    // Sort by severity first (high, medium, low), then by timestamp
    const severityOrder = { high: 3, medium: 2, low: 1 };
    const severityDiff = severityOrder[b.severity as keyof typeof severityOrder] - 
                        severityOrder[a.severity as keyof typeof severityOrder];
    if (severityDiff !== 0) return severityDiff;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  const handleToggleNotifications = () => {
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);
    onToggleNotifications?.(newState);
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Performance Alerts
              {alerts.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {alerts.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Real-time performance and safety notifications
            </CardDescription>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Notification Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleNotifications}
              className={notificationsEnabled ? 'text-green-600' : 'text-gray-400'}
            >
              {notificationsEnabled ? (
                <Bell className="w-4 h-4" />
              ) : (
                <BellOff className="w-4 h-4" />
              )}
            </Button>
            
            {/* Dismiss All */}
            {alerts.length > 0 && onDismissAll && (
              <Button variant="outline" size="sm" onClick={onDismissAll}>
                <Archive className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        {alerts.length > 0 && (
          <div className="flex items-center space-x-2 mt-3">
            <span className="text-xs text-muted-foreground">Filter by severity:</span>
            <div className="flex space-x-1">
              {['all', 'high', 'medium', 'low'].map((severity) => (
                <Button
                  key={severity}
                  variant={filterSeverity === severity ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterSeverity(severity as any)}
                  className="text-xs h-7"
                >
                  {severity.charAt(0).toUpperCase() + severity.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="p-0">
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No active alerts</p>
            <p className="text-sm">Performance monitoring is active</p>
          </div>
        ) : (
          <ScrollArea className="h-64">
            <div className="space-y-2 p-4">
              {sortedAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border transition-all duration-200 hover:shadow-sm ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getTypeIcon(alert.type)}</span>
                        {getSeverityIcon(alert.severity)}
                        <span className="font-medium text-sm">{alert.playerName}</span>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            alert.severity === 'high' ? 'border-red-300 text-red-700' :
                            alert.severity === 'medium' ? 'border-yellow-300 text-yellow-700' :
                            'border-blue-300 text-blue-700'
                          }`}
                        >
                          {alert.severity.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <p className="text-sm mb-2">{alert.message}</p>
                      
                      <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDistanceToNow(alert.timestamp, { addSuffix: true })}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDismissAlert(alert.id)}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}